import React, { useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, Download, Play, Save, Search } from 'lucide-react';
import { reportsAPI } from '../../api';
import { useAppStore } from '../../stores/useAppStore';
import { downloadResponse } from '../../utils/download';
import { Button, Input } from '../ui';
import {
  ColumnsSection,
  DateAndOptionsSection,
  FiltersSection,
  GroupBySection,
  MeasuresSection,
  SortSection,
} from './BuilderSections';
import FieldTree from './FieldTree';
import ResultsGrid from './ResultsGrid';
import SaveReportModal from './SaveReportModal';
import { extractApiError, summarise } from './lib/apiErrors';
import { filterTree, groupRef, indexDataset, labelFor } from './lib/catalog';
import { ACTIONS, definitionReducer, hashDefinition, normaliseDefinition, validateDefinition } from './lib/definition';
import { useReportCatalog } from './useReportCatalog';
import { useReportRun, userTimezone } from './useReportRun';
import { useUnsavedChangesGuard } from './useUnsavedChangesGuard';

const SELECT = 'rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm w-full';

/**
 * The guided report builder. Holds the definition JSON in a reducer, previews
 * it against POST /reports/preview and saves it as a Kind='builder' report.
 * `report` is passed when editing an existing one.
 */
const ReportBuilder = ({ report = null }) => {
  const navigate = useNavigate();
  const { addNotification } = useAppStore();
  const { catalog, datasets, datasetByKey, presets, buckets, opsByType, loading: catalogLoading, error: catalogError, reload } = useReportCatalog();

  const [definition, dispatch] = useReducer(definitionReducer, report?.Definition ? normaliseDefinition(report.Definition) : null);
  const [savedHash, setSavedHash] = useState(() => (report?.Definition ? hashDefinition(normaliseDefinition(report.Definition)) : null));
  const [savedMeta, setSavedMeta] = useState(report ? { id: report.ID, name: report.Name, description: report.Description || '' } : null);
  const [search, setSearch] = useState('');
  const [serverError, setServerError] = useState(null);
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveAsNew, setSaveAsNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [exporting, setExporting] = useState(null);

  const datasetNode = definition ? datasetByKey.get(definition.dataset) : null;
  const index = useMemo(() => indexDataset(datasetNode), [datasetNode]);
  const visibleTree = useMemo(() => filterTree(datasetNode, search), [datasetNode, search]);
  const validation = useMemo(() => (definition ? validateDefinition(definition, index, catalog) : { errors: {}, missingIds: [], isValid: false }), [definition, index, catalog]);

  const currentHash = definition ? hashDefinition(definition) : null;
  const isDirty = currentHash !== savedHash;
  const confirmLeave = useUnsavedChangesGuard(isDirty);

  const execute = useCallback((base) => reportsAPI.preview(definition, base), [definition]);
  const run = useReportRun({ execute, key: currentHash });
  const grouping = (definition?.group_by.length || 0) > 0;
  const groupRefs = useMemo(() => (definition ? definition.group_by.map(groupRef) : []), [definition]);

  const selected = useMemo(
    () => ({
      columns: new Set(definition?.columns || []),
      groupBy: new Set((definition?.group_by || []).map((g) => g.field)),
    }),
    [definition]
  );

  const doRun = useCallback(() => {
    if (!definition) return;
    if (!validation.isValid) {
      const firstSection = Object.entries(validation.errors).find(([, list]) => list.length)?.[0];
      addNotification({ type: 'error', message: validation.missingIds.length ? 'Remove the missing fields before running' : 'Fix the highlighted sections before running' });
      if (firstSection) document.querySelector(`[data-section-key="${firstSection}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    setServerError(null);
    run.run().then((res) => {
      if (!res && run.error) setServerError(run.error);
    });
  }, [definition, validation, run, addNotification]);

  useEffect(() => {
    if (run.status === 'error') setServerError(run.error);
    if (run.status === 'success') setServerError(null);
  }, [run.status, run.error]);

  useEffect(() => {
    const handler = (e) => {
      if (saveOpen) return;
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        e.preventDefault();
        doRun();
      } else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') {
        e.preventDefault();
        if (definition) openSave(false);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [doRun, saveOpen, definition]);

  const changeDataset = (key) => {
    const node = datasetByKey.get(key);
    if (!node) return;
    if (definition && isDirty && !window.confirm('Changing the dataset clears columns, filters, groups and measures. Continue?')) return;
    dispatch({ type: ACTIONS.SET_DATASET, dataset: node });
    run.reset();
    setServerError(null);
  };

  const addGroup = (id) => {
    if (!grouping && definition.columns.length && !window.confirm('Grouping replaces the current columns with the group-by fields. Continue?')) return;
    dispatch({ type: ACTIONS.ADD_GROUP_BY, id });
  };

  const openSave = (asNew) => {
    setSaveAsNew(asNew);
    setSaveError(null);
    setSaveOpen(true);
  };

  const submitSave = async (values) => {
    setSaving(true);
    setSaveError(null);
    const payload = { Name: values.name.trim(), Description: values.description?.trim() || null, Kind: 'builder', Definition: definition, SqlText: null, Parameters: null, IsActive: true };
    try {
      if (savedMeta?.id && !saveAsNew) {
        const res = await reportsAPI.update(savedMeta.id, { Name: payload.Name, Description: payload.Description, Definition: definition });
        setSavedMeta({ id: res.data.ID, name: res.data.Name, description: res.data.Description || '' });
        addNotification({ type: 'success', message: 'Report saved' });
      } else {
        const res = await reportsAPI.create(payload);
        setSavedMeta({ id: res.data.ID, name: res.data.Name, description: res.data.Description || '' });
        addNotification({ type: 'success', message: `Report "${res.data.Name}" created` });
        setSavedHash(currentHash);
        setSaveOpen(false);
        navigate(`/reports/${res.data.ID}/edit`, { replace: true });
        return;
      }
      setSavedHash(currentHash);
      setSaveOpen(false);
    } catch (err) {
      const apiErr = await extractApiError(err);
      setSaveError(summarise(apiErr));
      if (apiErr.status === 422) setServerError(apiErr);
    } finally {
      setSaving(false);
    }
  };

  const doExport = async (format) => {
    if (!validation.isValid) {
      addNotification({ type: 'error', message: 'Fix the highlighted sections before exporting' });
      return;
    }
    setExporting(format);
    try {
      const res = await reportsAPI.exportPreview(definition, { user_timezone: userTimezone() }, format);
      downloadResponse(res, `report-preview.${format}`);
    } catch (err) {
      addNotification({ type: 'error', message: summarise(await extractApiError(err)) });
    } finally {
      setExporting(null);
    }
  };

  const sectionErrors = (name) => validation.errors[name] || [];
  const highlighted = (name) => serverError?.section === name;
  const filterRowErrors = useMemo(() => {
    const map = {};
    [...sectionErrors('filters'), ...(serverError?.errors || []).filter((e) => e.startsWith('filters['))].forEach((e) => {
      const m = /^[Ff]ilters?\s*\[?(\d+)\]?/.exec(e);
      if (m) {
        const idx = Number(m[1]) - (e.startsWith('Filter ') ? 1 : 0);
        (map[idx] = map[idx] || []).push(e.replace(/^[^:]*:\s*/, ''));
      }
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validation, serverError]);

  const title = savedMeta?.name || 'New report';

  if (catalogError) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center">
        <p className="text-sm text-red-700 mb-4">{catalogError}</p>
        <Button onClick={reload}>Retry</Button>
      </div>
    );
  }

  return (
    <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-4 space-y-4">
      {/* Top bar */}
      <div className="sticky top-16 z-20 bg-gray-50/95 backdrop-blur py-2 flex flex-wrap items-center justify-between gap-3 border-b border-gray-200">
        <div className="flex items-center gap-3 min-w-0">
          <button
            type="button"
            onClick={() => {
              if (confirmLeave()) navigate('/reports');
            }}
            className="inline-flex items-center text-sm text-gray-500 hover:text-gray-800"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Reports
          </button>
          <h1 className="text-xl font-bold text-gray-900 truncate flex items-center gap-2">
            {title}
            {isDirty && <span className="w-2 h-2 rounded-full bg-amber-500" title="Unsaved changes" />}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" disabled={!definition || !!exporting} onClick={() => doExport('csv')} title="Export current definition as CSV">
            <Download className="w-4 h-4 mr-1" /> {exporting === 'csv' ? '…' : 'CSV'}
          </Button>
          <Button variant="secondary" disabled={!definition || !!exporting} onClick={() => doExport('xlsx')} title="Export current definition as Excel">
            <Download className="w-4 h-4 mr-1" /> {exporting === 'xlsx' ? '…' : 'Excel'}
          </Button>
          {savedMeta?.id && (
            <Button variant="secondary" disabled={!definition} onClick={() => openSave(true)}>
              Save as new
            </Button>
          )}
          <Button variant="secondary" disabled={!definition} onClick={() => openSave(false)} title="Ctrl+S">
            <Save className="w-4 h-4 mr-1" /> Save
          </Button>
          <Button onClick={doRun} disabled={!definition || run.isRunning} title={validation.isValid ? 'Ctrl+Enter' : Object.values(validation.errors).flat()[0] || 'Remove missing fields'}>
            <Play className="w-4 h-4 mr-1" /> {run.isRunning ? 'Running…' : 'Run'}
          </Button>
        </div>
      </div>

      {validation.missingIds.length > 0 && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            These fields no longer exist in the dataset: <span className="font-mono">{validation.missingIds.join(', ')}</span>. Remove them to run the report.
          </div>
        </div>
      )}
      {serverError && !serverError.section && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {serverError.message}
          {serverError.errors?.length > 0 && (
            <ul className="list-disc ml-5 mt-1">
              {serverError.errors.map((e, i) => (
                <li key={i}>{e}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="xl:grid xl:grid-cols-[320px_minmax(0,1fr)] gap-4 space-y-4 xl:space-y-0">
        {/* Left: dataset + field tree */}
        <aside className="bg-white rounded-lg border border-gray-200 shadow-sm p-3 space-y-3 xl:sticky xl:top-32 xl:self-start xl:max-h-[calc(100vh-9rem)] flex flex-col">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Dataset</label>
            <select className={SELECT} value={definition?.dataset || ''} onChange={(e) => changeDataset(e.target.value)} disabled={catalogLoading}>
              <option value="">{catalogLoading ? 'Loading…' : 'Choose a dataset…'}</option>
              {datasets.map((d) => (
                <option key={d.key} value={d.key}>
                  {d.label}
                </option>
              ))}
            </select>
            {datasetNode?.description && <p className="mt-1 text-xs text-gray-500">{datasetNode.description}</p>}
          </div>
          {datasetNode && (
            <>
              <div className="relative">
                <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input className="pl-9" placeholder="Search fields…" value={search} onChange={(e) => setSearch(e.target.value)} />
              </div>
              <div className="overflow-auto flex-1 -mx-1 px-1">
                <FieldTree
                  dataset={visibleTree}
                  selected={selected}
                  groupingActive={grouping}
                  onAddColumn={(id) => dispatch({ type: ACTIONS.ADD_COLUMN, id })}
                  onAddGroup={addGroup}
                  onAddFilter={(field, fieldType) => dispatch({ type: ACTIONS.ADD_FILTER, field, fieldType, opsByType })}
                  onAddMeasure={(rollupId) => dispatch({ type: ACTIONS.ADD_ADHOC_MEASURE, field: rollupId, agg: 'sum' })}
                />
              </div>
              <p className="text-[11px] text-gray-400">Hover a field for actions: column, group, filter. Σ rollups aggregate related records.</p>
            </>
          )}
          {!datasetNode && !catalogLoading && datasets.length === 0 && <p className="text-sm text-gray-500">No datasets are configured.</p>}
        </aside>

        {/* Centre: sections + preview */}
        <main className="space-y-4 min-w-0">
          {!definition && (
            <div className="border-2 border-dashed border-gray-200 rounded-lg p-12 text-center text-gray-500 text-sm">
              Choose a dataset on the left to start building a report.
            </div>
          )}
          {definition && (
            <>
              <div data-section-key="columns">
                <ColumnsSection definition={definition} index={index} dispatch={dispatch} errors={sectionErrors('columns')} highlighted={highlighted('columns')} />
              </div>
              <div className="grid gap-4 lg:grid-cols-2">
                <div data-section-key="group_by">
                  <GroupBySection definition={definition} index={index} dispatch={dispatch} errors={sectionErrors('group_by')} highlighted={highlighted('group_by')} buckets={buckets} />
                </div>
                <div data-section-key="measures">
                  <MeasuresSection definition={definition} index={index} dispatch={dispatch} errors={sectionErrors('measures')} highlighted={highlighted('measures')} catalog={catalog} />
                </div>
              </div>
              <div data-section-key="filters">
                <FiltersSection
                  definition={definition}
                  index={index}
                  dispatch={dispatch}
                  errors={sectionErrors('filters').filter((e) => !/^Filter \d+/.test(e))}
                  rowErrors={filterRowErrors}
                  highlighted={highlighted('filters')}
                  opsByType={opsByType}
                />
              </div>
              <div className="grid gap-4 lg:grid-cols-2">
                <div data-section-key="sort">
                  <SortSection definition={definition} index={index} dispatch={dispatch} errors={sectionErrors('sort')} highlighted={highlighted('sort')} />
                </div>
                <div data-section-key="date_range">
                  <DateAndOptionsSection
                    definition={definition}
                    index={index}
                    dispatch={dispatch}
                    errors={[...sectionErrors('date_range'), ...(serverError?.section === 'options' ? serverError.errors : [])]}
                    highlighted={highlighted('date_range') || highlighted('options')}
                    presets={presets}
                    datasetNode={datasetNode}
                    applied={run.result?.date_range_applied}
                  />
                </div>
              </div>

              {/* Preview */}
              <section className="space-y-2">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                    Preview
                    {run.isStale && run.result && <span className="text-xs font-normal rounded bg-amber-100 text-amber-800 px-2 py-0.5">Definition changed — run to refresh</span>}
                  </h2>
                  <div className="flex items-center gap-3 text-xs text-gray-500">
                    {run.result && (
                      <>
                        <span>{run.result.total_rows != null ? `${run.result.total_rows.toLocaleString()} rows` : ''}</span>
                        <span>generated {new Date(run.result.generated_at).toLocaleTimeString()}</span>
                      </>
                    )}
                  </div>
                </div>
                <ResultsGrid
                  result={run.result}
                  status={run.status}
                  error={run.error}
                  groupBy={groupRefs}
                  sort={definition.sort}
                  onSort={(colId) => {
                    const { entry } = labelFor(index, colId);
                    if (!entry && !definition.measures.length) return;
                    dispatch({ type: ACTIONS.TOGGLE_SORT_FROM_GRID, by: colId });
                  }}
                  page={run.page}
                  pageSize={run.pageSize}
                  onPageChange={(p) => run.setPage(p)}
                  onPageSizeChange={(s) => run.setPageSize(s)}
                  stale={run.isStale}
                  emptyHint="Run (Ctrl+Enter) to preview the report"
                />
              </section>
            </>
          )}
        </main>
      </div>

      <SaveReportModal
        isOpen={saveOpen}
        onClose={() => setSaveOpen(false)}
        title={savedMeta?.id && !saveAsNew ? 'Save report' : 'Save as new report'}
        initialValues={saveAsNew ? { name: savedMeta ? `${savedMeta.name} (copy)` : '', description: savedMeta?.description || '' } : { name: savedMeta?.name || '', description: savedMeta?.description || '' }}
        onSubmit={submitSave}
        serverError={saveError}
        submitting={saving}
      />
    </div>
  );
};

export default ReportBuilder;
